// GradeLetter — letter + label panel paired with the new
// PerformanceGrade composite. Two variants:
//   `large`   — bordered aspect-square tile inside the Details
//               Performance card. Stack: 64px letter, tier-coloured
//               label pill, "Score N of 100" sub-line. Info toggle
//               anchored to the top-right corner; info panel renders
//               as a sibling beneath the tile so the square aspect is
//               preserved when the panel opens.
//   `compact` — Contract Register grade column (small coloured badge
//               with the letter only — no label).
//
// The locked label vocabulary is sourced from contractGrade.GRADE_LABELS
// so changes ripple through both surfaces in lockstep. Awaiting-data
// renders an em-dash letter and the "Awaiting data" label/badge text.
//
// Click-toggle info panel (large variant only) mirrors the
// MeetingSummaryStrip "held on time" pattern and explains how the
// grade was computed: letter + label, score, applied cap reason
// when present, and which pillars are critical.

import {
  GRADE_LABELS,
  AWAITING_DATA_LABEL,
  type Grade,
  type GradeResult,
} from "../../../lib/contract-overview/contractGrade";

export type GradeLetterVariant = "large" | "compact";

interface GradeLetterProps {
  result: GradeResult;
  variant?: GradeLetterVariant;
  /** Override the displayed grade word (badge title/aria-label and the large
   *  pill). Supplier surfaces read grades in the supplier vocabulary
   *  (Strong/Fair/…) rather than the contract pillar labels, so the badge text
   *  must match the visible word beside it. Ignored when the grade is null —
   *  the awaiting-data label always wins. */
  label?: string;
}

interface TierStyles {
  /** Tailwind classes for the tinted panel surface (large variant). */
  panel: string;
  /** Tier-coloured border for the large-variant tile. */
  border: string;
  /** Solid background for compact badge. */
  badge: string;
  /** Letter foreground colour. */
  letter: string;
  /** Large-variant label-pill background. */
  pillBg: string;
  /** Large-variant label-pill foreground. */
  pillText: string;
}

// 15% opacity panel tint per tier — locked palette. Light mode uses
// the brand-aligned tier colour at /15 alpha; dark mode uses /20 to
// keep contrast against slate-900 surfaces. Letter colour pairs with
// the panel so the grade reads as part of the surface, not floating.
// Border opacity sits at /40–/50 to give the tile a clear ring without
// crossing into a hard outline. Pill bg/text reuse the badge palette so
// the categorical label inside the large tile reads identically to the
// compact-variant badge elsewhere in the app.
const TIER_STYLES: Record<Grade, TierStyles> = {
  A: {
    panel: "bg-emerald-500/15 dark:bg-emerald-500/20",
    border: "border-emerald-500/40 dark:border-emerald-400/40",
    badge:
      "bg-emerald-500 text-white dark:bg-emerald-400 dark:text-emerald-950",
    letter: "text-emerald-700 dark:text-emerald-300",
    pillBg: "bg-emerald-500 dark:bg-emerald-400",
    pillText: "text-white dark:text-emerald-950",
  },
  B: {
    panel: "bg-emerald-300/20 dark:bg-emerald-400/15",
    border: "border-emerald-400/40 dark:border-emerald-300/40",
    badge:
      "bg-emerald-300 text-emerald-950 dark:bg-emerald-300 dark:text-emerald-950",
    letter: "text-emerald-700 dark:text-emerald-300",
    pillBg: "bg-emerald-400 dark:bg-emerald-300",
    pillText: "text-emerald-950 dark:text-emerald-950",
  },
  C: {
    panel: "bg-amber-500/15 dark:bg-amber-500/20",
    border: "border-amber-500/50 dark:border-amber-400/50",
    badge: "bg-amber-500 text-white dark:bg-amber-400 dark:text-amber-950",
    letter: "text-amber-700 dark:text-amber-300",
    pillBg: "bg-amber-500 dark:bg-amber-400",
    pillText: "text-white dark:text-amber-950",
  },
  D: {
    panel: "bg-orange-500/15 dark:bg-orange-500/20",
    border: "border-orange-500/50 dark:border-orange-400/50",
    badge: "bg-orange-500 text-white dark:bg-orange-400 dark:text-orange-950",
    letter: "text-orange-700 dark:text-orange-300",
    pillBg: "bg-orange-500 dark:bg-orange-400",
    pillText: "text-white dark:text-orange-950",
  },
  E: {
    panel: "bg-red-500/15 dark:bg-red-500/20",
    border: "border-red-500/50 dark:border-red-400/50",
    badge: "bg-red-600 text-white dark:bg-red-500 dark:text-red-50",
    letter: "text-red-700 dark:text-red-300",
    pillBg: "bg-red-600 dark:bg-red-500",
    pillText: "text-white dark:text-red-50",
  },
};

const AWAITING_STYLES: TierStyles = {
  panel: "bg-slate-200/40 dark:bg-slate-800/40",
  border: "border-slate-300/40 dark:border-slate-700/60",
  badge: "bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  letter: "text-slate-500 dark:text-slate-400",
  pillBg: "bg-slate-200 dark:bg-slate-700",
  pillText: "text-slate-700 dark:text-slate-200",
};

function tierFor(grade: Grade | null): TierStyles {
  return grade ? TIER_STYLES[grade] : AWAITING_STYLES;
}

export function GradeLetter({
  result,
  variant = "large",
  label: labelOverride,
}: GradeLetterProps) {
  const tier = tierFor(result.grade);
  const letter = result.grade ?? "—";
  const label = result.grade
    ? (labelOverride ?? GRADE_LABELS[result.grade])
    : AWAITING_DATA_LABEL;

  if (variant === "compact") {
    return (
      <span
        data-testid="grade-letter-compact"
        data-grade={result.grade ?? "awaiting"}
        title={result.grade ? `${letter} — ${label}` : AWAITING_DATA_LABEL}
        className={`inline-flex h-7 min-w-[1.75rem] items-center justify-center rounded-md px-1.5 text-body-sm font-bold ${tier.badge}`}
        aria-label={
          result.grade
            ? `Grade ${letter} — ${label}`
            : `Grade: ${AWAITING_DATA_LABEL}`
        }
      >
        {letter}
      </span>
    );
  }

  return (
    <GradeLetterLarge
      result={result}
      tier={tier}
      letter={letter}
      label={label}
    />
  );
}

function GradeLetterLarge({
  result,
  tier,
  letter,
  label,
}: {
  result: GradeResult;
  tier: TierStyles;
  letter: string;
  label: string;
}) {
  return (
    <div
      data-testid="grade-letter-large"
      data-grade={result.grade ?? "awaiting"}
      className={`flex aspect-square w-full flex-col items-center justify-center rounded-lg border p-4 ${tier.panel} ${tier.border}`}
    >
      <span
        data-testid="grade-letter-large-letter"
        className={`text-[64px] leading-none font-bold ${tier.letter}`}
      >
        {letter}
      </span>
      <div
        data-testid="grade-letter-large-label"
        className={`mt-2 inline-flex items-center rounded-md px-2.5 py-1 text-overline uppercase ${tier.pillBg} ${tier.pillText}`}
      >
        {label}
      </div>
      <div
        data-testid="grade-letter-large-score"
        className="mt-1.5 text-caption text-slate-500 dark:text-slate-400"
      >
        {result.score === null
          ? "Score awaiting data"
          : `Score ${result.score} of 100`}
      </div>
    </div>
  );
}

export default GradeLetter;
